import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import {
  calculateRoute,
  TransportMode,
  RoutePoint,
  Route,
  RoutingResult,
  RoutingOptions,
} from '../services/routing';

// ============================================
// TYPES
// ============================================

export interface RouteContextState {
  // Mode state
  isRoutingMode: boolean;
  setIsRoutingMode: (value: boolean) => void;

  // Route points
  origin: RoutePoint | null;
  setOrigin: (point: RoutePoint | null) => void;
  destination: RoutePoint | null;
  setDestination: (point: RoutePoint | null) => void;
  waypoints: RoutePoint[];
  setWaypoints: (points: RoutePoint[]) => void;

  // Route result
  routingResult: RoutingResult | null;
  selectedRouteIndex: number;
  setSelectedRouteIndex: (index: number) => void;
  selectedRoute: Route | null;

  // Transport mode
  transportMode: TransportMode;
  setTransportMode: (mode: TransportMode) => void;

  // Loading & error states
  isCalculating: boolean;
  error: string | null;

  // Actions
  addWaypoint: (point: RoutePoint, index?: number) => void;
  removeWaypoint: (index: number) => void;
  reorderWaypoints: (fromIndex: number, toIndex: number) => void;
  swapOriginDestination: () => void;
  calculateCurrentRoute: () => Promise<void>;
  clearRoute: () => void;
  exitRoutingMode: () => void;

  // Start routing mode with destination
  startRouteToDestination: (destination: RoutePoint, origin?: RoutePoint) => void;
}

// ============================================
// CONTEXT
// ============================================

const RouteContext = createContext<RouteContextState | undefined>(undefined);

// ============================================
// PROVIDER
// ============================================

interface RouteProviderProps {
  children: ReactNode;
  userPosition?: [number, number] | null;
  userInfo?: {
    firstName?: string;
    lastName?: string;
    profilePhoto?: string;
    address?: {
      latitude?: number;
      longitude?: number;
      street_address?: string;
      city?: string;
    };
  } | null;
}

export const RouteProvider: React.FC<RouteProviderProps> = ({
  children,
  userPosition,
  userInfo,
}) => {
  // Mode state
  const [isRoutingMode, setIsRoutingMode] = useState(false);

  // Route points
  const [origin, setOrigin] = useState<RoutePoint | null>(null);
  const [destination, setDestination] = useState<RoutePoint | null>(null);
  const [waypoints, setWaypoints] = useState<RoutePoint[]>([]);

  // Route result
  const [routingResult, setRoutingResult] = useState<RoutingResult | null>(null);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);

  // Transport mode
  const [transportMode, setTransportModeState] = useState<TransportMode>('driving');

  // Wrapper for transport mode changes
  const setTransportMode = useCallback((mode: TransportMode) => {
    setTransportModeState(mode);
  }, []);

  // Loading & error states
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get selected route
  const selectedRoute = routingResult?.routes?.[selectedRouteIndex] || null;

  // Set default origin from user position/address when entering routing mode
  useEffect(() => {
    if (isRoutingMode && !origin) {
      if (userPosition) {
        setOrigin({
          lat: userPosition[0],
          lng: userPosition[1],
          name: 'Ma position',
          markerType: 'gps',
        });
      } else if (userInfo?.address?.latitude && userInfo?.address?.longitude) {
        setOrigin({
          lat: userInfo.address.latitude,
          lng: userInfo.address.longitude,
          name: 'Mon adresse',
          address: userInfo.address.street_address
            ? `${userInfo.address.street_address}, ${userInfo.address.city}`
            : userInfo.address.city,
          markerType: 'userAddress',
          profilePhoto: userInfo.profilePhoto,
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
        });
      }
    }
  }, [isRoutingMode, origin, userPosition, userInfo]);

  // Actions
  const addWaypoint = useCallback((point: RoutePoint, index?: number) => {
    setWaypoints(prev => {
      if (index !== undefined && index >= 0 && index <= prev.length) {
        const newWaypoints = [...prev];
        newWaypoints.splice(index, 0, point);
        return newWaypoints;
      }
      return [...prev, point];
    });
  }, []);

  const removeWaypoint = useCallback((index: number) => {
    setWaypoints(prev => prev.filter((_, i) => i !== index));
  }, []);

  const reorderWaypoints = useCallback((fromIndex: number, toIndex: number) => {
    setWaypoints(prev => {
      const newWaypoints = [...prev];
      const [moved] = newWaypoints.splice(fromIndex, 1);
      newWaypoints.splice(toIndex, 0, moved);
      return newWaypoints;
    });
  }, []);

  const swapOriginDestination = useCallback(() => {
    const temp = origin;
    setOrigin(destination);
    setDestination(temp);
    // Reverse waypoints order too
    setWaypoints(prev => [...prev].reverse());
  }, [origin, destination]);

  const calculateCurrentRoute = useCallback(async () => {
    if (!origin || !destination) {
      setError('Veuillez définir un point de départ et une destination');
      return;
    }

    setIsCalculating(true);
    setError(null);

    try {
      const options: RoutingOptions = {
        mode: transportMode,
        alternatives: true,
        steps: true,
        language: 'fr',
      };

      const { result, error: routingError } = await calculateRoute(origin, destination, waypoints, options);

      if (result && result.routes.length > 0) {
        setRoutingResult(result);
        setSelectedRouteIndex(0);
      } else if (routingError) {
        setError(routingError.message);
        setRoutingResult(null);
      } else {
        setError('Aucun itinéraire trouvé');
        setRoutingResult(null);
      }
    } catch (err) {
      console.error('Route calculation error:', err);
      setError('Erreur lors du calcul de l\'itinéraire');
      setRoutingResult(null);
    } finally {
      setIsCalculating(false);
    }
  }, [origin, destination, waypoints, transportMode]);

  // Recalculate route when points or mode change
  useEffect(() => {
    if (isRoutingMode && origin && destination) {
      calculateCurrentRoute();
    }
  }, [isRoutingMode, origin, destination, waypoints, transportMode, calculateCurrentRoute]);

  const clearRoute = useCallback(() => {
    setRoutingResult(null);
    setSelectedRouteIndex(0);
    setError(null);
  }, []);

  const exitRoutingMode = useCallback(() => {
    setIsRoutingMode(false);
    setOrigin(null);
    setDestination(null);
    setWaypoints([]);
    setRoutingResult(null);
    setSelectedRouteIndex(0);
    setError(null);
  }, []);

  const startRouteToDestination = useCallback((dest: RoutePoint, orig?: RoutePoint) => {
    setIsRoutingMode(true);
    setDestination(dest);

    if (orig) {
      setOrigin(orig);
    } else if (userPosition) {
      // GPS position as origin
      setOrigin({
        lat: userPosition[0],
        lng: userPosition[1],
        name: 'Ma position',
        markerType: 'gps',
      });
    } else if (userInfo?.address?.latitude && userInfo?.address?.longitude) {
      // User's home address as origin
      setOrigin({
        lat: userInfo.address.latitude,
        lng: userInfo.address.longitude,
        name: 'Mon adresse',
        address: userInfo.address.street_address
          ? `${userInfo.address.street_address}, ${userInfo.address.city}`
          : userInfo.address.city,
        markerType: 'userAddress',
        profilePhoto: userInfo.profilePhoto,
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
      });
    }

    setWaypoints([]);
    setRoutingResult(null);
    setSelectedRouteIndex(0);
    setError(null);
  }, [userPosition, userInfo]);

  const value: RouteContextState = {
    isRoutingMode,
    setIsRoutingMode,
    origin,
    setOrigin,
    destination,
    setDestination,
    waypoints,
    setWaypoints,
    routingResult,
    selectedRouteIndex,
    setSelectedRouteIndex,
    selectedRoute,
    transportMode,
    setTransportMode,
    isCalculating,
    error,
    addWaypoint,
    removeWaypoint,
    reorderWaypoints,
    swapOriginDestination,
    calculateCurrentRoute,
    clearRoute,
    exitRoutingMode,
    startRouteToDestination,
  };

  return (
    <RouteContext.Provider value={value}>
      {children}
    </RouteContext.Provider>
  );
};

// ============================================
// HOOK
// ============================================

export function useRoute(): RouteContextState {
  const context = useContext(RouteContext);
  if (context === undefined) {
    throw new Error('useRoute must be used within a RouteProvider');
  }
  return context;
}

export default RouteContext;
